/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { TagProps } from '@coursera/cds-core';
import { Tag } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/product-card';

type ColorCodedPillTag = {
  text: string;
  tagProps?: Partial<TagProps>;
};

// A single, pre-rendered experimental pill (e.g. from getTopAiProgramExperimentalTag). `tag` is
// the exact translated text that also appears in `pillTags`, used only to find its slot; the
// caller owns everything about how it renders.
export type ExperimentalPillTag = {
  tag: string;
  tagRenderer: React.ReactNode;
};

type Props = {
  pillTags: ColorCodedPillTag[];
  topMatchLabel?: string;
  // A list since multiple independent pill experiments can be active on the same card at once.
  experimentalPillTags?: ExperimentalPillTag[];
};

const getTranslatedPillTagText = (text: string) => {
  const translationMap: Record<string, string> = {
    new: _t('New'),
    free: _t('Free'),
    popular: _t('Popular'),
    'free trial': _t('Free trial'),
    preview: _t('Preview'),
    'fully translated': _t('Fully translated'),
    subtitles: _t('Subtitles'),
    'top instructor': _t('Top instructor'),
    'ai skills': _t('AI skills'),
    'build toward a degree': _t('Build toward a degree'),
    'earn a degree': _t('Earn a degree'),
    'credit offered': _t('Credit offered'),
    plus: _t('Plus'),
    lite: _t('Lite'),
  };
  const lowercaseText = text.toLowerCase();
  return translationMap[lowercaseText] ?? text;
};

const getPillTagIcon = (text: string) => {
  switch (text.toLowerCase()) {
    case 'ai skills':
      return <AIGenerateIcon size="small" />;
    default:
      return undefined;
  }
};

const getPillTagProps = (pillTag: ColorCodedPillTag): Partial<TagProps> => {
  const { text: untranslatedText, tagProps } = pillTag;
  const translatedText = getTranslatedPillTagText(untranslatedText);
  const icon = getPillTagIcon(untranslatedText);

  const defaultTagProps: Partial<TagProps> = {
    variant: 'category',
    priority: 'secondary',
    color: 'default',
    icon,
    children: translatedText,
    ...tagProps,
  };

  const lowerText = untranslatedText.toLowerCase();

  if (lowerText.includes('free trial') || lowerText === 'plus' || lowerText === 'lite') {
    return {
      ...defaultTagProps,
      priority: 'secondary',
      color: 'highlight1',
      variant: 'status',
    };
  }

  return defaultTagProps;
};

const styles = {
  container: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
  `,
};

/**
 * Renders a list of color-coded pill tags using CDS Tag components.
 * @param {ColorCodedPillTag[]} props.pillTags - A list of pill tags to render. If an item in the list
 * is an untranslated string, this component will attempt to automatically translate the text and color
 * code the tag based on the text.
 */
const LearningProductCardColorCodedPillTags = ({ pillTags, topMatchLabel, experimentalPillTags }: Props) => {
  if (!pillTags || pillTags.length === 0) {
    return null;
  }

  return (
    <div css={styles.container}>
      {pillTags.map((pillTag) => {
        if (topMatchLabel && pillTag.text === topMatchLabel) {
          return (
            <Tag
              key={pillTag.text}
              color="ai"
              data-testid="learning-product-card-top-match-pill"
              icon={<AIGenerateIcon size="small" />}
              priority="secondary"
              variant="status"
            >
              {pillTag.text}
            </Tag>
          );
        }

        const experimentalPillTag = experimentalPillTags?.find((candidate) => candidate.tag === pillTag.text);
        if (experimentalPillTag) {
          return <React.Fragment key={pillTag.text}>{experimentalPillTag.tagRenderer}</React.Fragment>;
        }

        const { children, ...tagProps } = getPillTagProps(pillTag);
        return (
          <Tag key={pillTag.text} {...tagProps}>
            {children ?? pillTag.text}
          </Tag>
        );
      })}
    </div>
  );
};

export default LearningProductCardColorCodedPillTags;
